// 2D affine transforms, stored as the six values of an SVG matrix:
//
//   | a c e |
//   | b d f |
//   | 0 0 1 |
//
// Kept as plain six-element arrays rather than objects because the renderer
// multiplies them thousands of times a frame and allocates a fresh one for every
// node it bakes.

export function identity() {
  return [1, 0, 0, 1, 0, 0];
}

export function isIdentity(m) {
  return (
    m[0] === 1 &&
    m[1] === 0 &&
    m[2] === 0 &&
    m[3] === 1 &&
    m[4] === 0 &&
    m[5] === 0
  );
}

// out = a * b, i.e. apply b first then a, matching SVG's nesting order.
export function multiply(a, b, out) {
  const a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5];
  const b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3],
    b4 = b[4],
    b5 = b[5];

  const r = out || new Array(6);
  r[0] = a0 * b0 + a2 * b1;
  r[1] = a1 * b0 + a3 * b1;
  r[2] = a0 * b2 + a2 * b3;
  r[3] = a1 * b2 + a3 * b3;
  r[4] = a0 * b4 + a2 * b5 + a4;
  r[5] = a1 * b4 + a3 * b5 + a5;
  return r;
}

export function applyX(m, x, y) {
  return m[0] * x + m[2] * y + m[4];
}

export function applyY(m, x, y) {
  return m[1] * x + m[3] * y + m[5];
}

export function invert(m) {
  const det = m[0] * m[3] - m[1] * m[2];
  if (!det) return identity();
  const id = 1 / det;
  return [
    m[3] * id,
    -m[1] * id,
    -m[2] * id,
    m[0] * id,
    (m[2] * m[5] - m[3] * m[4]) * id,
    (m[1] * m[4] - m[0] * m[5]) * id,
  ];
}

// The factor by which the matrix scales lengths, averaged over direction. Used to
// pick a flattening tolerance and to widen antialiasing for shapes that are baked
// into a batch in one space and drawn in another.
export function meanScale(m) {
  const sx = Math.hypot(m[0], m[1]);
  const sy = Math.hypot(m[2], m[3]);
  return (sx + sy) / 2;
}

export function isAxisAligned(m) {
  // Tolerates the tiny off-diagonal values that fall out of a rotate(0) or a
  // scale composed through several parents.
  return Math.abs(m[1]) < 1e-6 && Math.abs(m[2]) < 1e-6;
}

const TRANSFORM_RE = /([a-zA-Z]+)\s*\(([^)]*)\)/g;
const NUMBER_RE = /[-+]?[0-9]*\.?[0-9]+(?:[eE][-+]?[0-9]+)?/g;

// Parsed transform strings are cached because the same handful of values recur
// across every object in the estate — `translate(0,-1)`, `rotate(-135)` and so on
// — and layout rewrites them every frame.
const transformCache = new Map();
const TRANSFORM_CACHE_LIMIT = 4096;

export function parseTransform(value) {
  if (!value) return null;

  const cached = transformCache.get(value);
  if (cached) return cached;

  const result = parseTransformUncached(value);

  // Positions vary continuously, so the cache would otherwise grow without bound.
  // Dropping it wholesale keeps the common repeated values cheap without needing
  // an eviction order.
  if (transformCache.size >= TRANSFORM_CACHE_LIMIT) transformCache.clear();
  transformCache.set(value, result);

  return result;
}

function parseTransformUncached(value) {
  let m = null;
  TRANSFORM_RE.lastIndex = 0;

  let match;
  while ((match = TRANSFORM_RE.exec(value)) !== null) {
    const args = match[2].match(NUMBER_RE);
    const n = args ? args.map(Number) : [];
    let step = null;

    switch (match[1]) {
      case "translate":
        step = [1, 0, 0, 1, n[0] || 0, n[1] || 0];
        break;
      case "scale": {
        const sx = n.length ? n[0] : 1;
        const sy = n.length > 1 ? n[1] : sx;
        step = [sx, 0, 0, sy, 0, 0];
        break;
      }
      case "rotate": {
        const rad = ((n[0] || 0) * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        if (n.length > 2) {
          const cx = n[1];
          const cy = n[2];
          step = [
            cos,
            sin,
            -sin,
            cos,
            cx - cos * cx + sin * cy,
            cy - sin * cx - cos * cy,
          ];
        } else {
          step = [cos, sin, -sin, cos, 0, 0];
        }
        break;
      }
      case "matrix":
        if (n.length >= 6) step = [n[0], n[1], n[2], n[3], n[4], n[5]];
        break;
      case "skewX":
        step = [1, 0, Math.tan(((n[0] || 0) * Math.PI) / 180), 1, 0, 0];
        break;
      case "skewY":
        step = [1, Math.tan(((n[0] || 0) * Math.PI) / 180), 0, 1, 0, 0];
        break;
      default:
        break;
    }

    if (step) m = m ? multiply(m, step) : step;
  }

  return m;
}
